import { Menu } from '@headlessui/react';
import {
  ArrowLeftStartOnRectangleIcon,
  Bars3Icon,
  CogIcon,
  NewspaperIcon,
  SparklesIcon,
  UserGroupIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { Link, useLocation, useNavigate, useSearchParams, useSubmit } from '@remix-run/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Dropdown from '~/components/Dropdown';
import Text from '~/components/Text';
import UpgradePageLink from '~/components/UpgradePageLink';

import type { Publication } from '~/interfaces/publication';

import LoginModal from './LoginModal';

interface Props {
  subscriberId?: string;
  avatarUrl?: string;
  isLoggedIn?: boolean;
  isPremiumEnabled?: boolean;
  isReferralEnabled?: boolean;
  isPremiumTierSubscriber?: boolean;
  publication: Publication;
}

const AccountDropdown = ({
  subscriberId,
  isReferralEnabled,
  avatarUrl,
  isPremiumEnabled,
  isPremiumTierSubscriber,
  isLoggedIn,
  publication,
}: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const submit = useSubmit();
  const hasAuthorsPage = publication?.web_template?.author_ids?.length >= 1;
  const hasMultiplePublications = publication?.web_template?.multi_pub_ids?.length >= 1;

  const handleCloseModal = () => {
    if (searchParams.has('email')) {
      navigate(location.pathname);
    }

    setIsOpen(false);
  };

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const handleLogout = () => {
    const formData = new FormData();
    formData.append('pathname', window?.location?.pathname);

    submit(formData, {
      method: 'post',
      action: '/logout',
    });
  };

  return (
    <>
      {!subscriberId && <LoginModal isOpen={isOpen} handleCloseModal={handleCloseModal} publication={publication} />}
      <Dropdown
        buttonChild={
          <div className="mt-1.5">
            {isLoggedIn ? (
              <div className="h-6 w-6 overflow-hidden rounded-full">
                <img className="h-6 w-6 rounded-full" src={avatarUrl} alt="The logged in user" />
              </div>
            ) : (
              <Bars3Icon className="h-6 w-6 stroke-1 text-wt-text-on-tertiary opacity-60 hover:opacity-100" />
            )}
          </div>
        }
      >
        {isPremiumEnabled && !isPremiumTierSubscriber && (
          <Menu.Item>
            {/* biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility */}
            {({ active }) => (
              <UpgradePageLink href={publication.web_template.external_upgrade_page_href}>
                <div className="group flex items-center space-x-2 rounded-wt px-3 py-2 hover:bg-black/5">
                  <SparklesIcon className="h-3 w-3 text-wt-text-on-tertiary" />
                  <Text as="span" type="title" theme="navs" className="whitespace-nowrap text-xs sm:block">
                    {t('nav.links.upgrade')}
                  </Text>
                </div>
              </UpgradePageLink>
            )}
          </Menu.Item>
        )}

        {hasMultiplePublications && (
          <Menu.Item>
            {/* biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility */}
            {({ active }) => (
              <Link to={'/publications'} prefetch="intent">
                <div className="group flex items-center space-x-2 rounded-wt px-3 py-2 hover:bg-black/5">
                  <NewspaperIcon className="h-3 w-3 text-wt-text-on-tertiary" />
                  <Text as="span" type="title" theme="navs" className="whitespace-nowrap text-xs sm:block">
                    Newsletters
                  </Text>
                </div>
              </Link>
            )}
          </Menu.Item>
        )}
        {hasAuthorsPage && (
          <Menu.Item>
            {/* biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility */}
            {({ active }) => (
              <Link to={'/authors'} prefetch="intent">
                <div className="group flex items-center space-x-2 rounded-wt px-3 py-2 hover:bg-black/5">
                  <UserGroupIcon className="h-3 w-3 text-wt-text-on-tertiary" />
                  <Text as="span" type="title" theme="navs" className="whitespace-nowrap text-xs sm:block">
                    {t('author.authors')}
                  </Text>
                </div>
              </Link>
            )}
          </Menu.Item>
        )}
        {subscriberId && (
          <Menu.Item>
            {/* biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility */}
            {({ active }) => (
              <Link to={`/subscribe/${subscriberId}/manage`} prefetch="intent">
                <div className="group flex items-center space-x-2 rounded-wt px-3 py-2 hover:bg-black/5">
                  <CogIcon className="h-3 w-3 text-wt-text-on-tertiary" />
                  <Text as="span" type="title" theme="navs" className="whitespace-nowrap text-xs sm:block">
                    {t('nav.links.manage')}
                  </Text>
                </div>
              </Link>
            )}
          </Menu.Item>
        )}
        {isReferralEnabled && subscriberId && (
          <Menu.Item>
            {/* biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility */}
            {({ active }) => (
              <Link to={`/subscribe/${subscriberId}/referrals`} prefetch="intent">
                <div className="group flex items-center space-x-2 rounded-wt px-3 py-2 hover:bg-black/5">
                  <UserPlusIcon className="h-3 w-3 text-wt-text-on-tertiary" />
                  <Text as="span" type="title" theme="navs" className="whitespace-nowrap text-xs sm:block">
                    {t('nav.links.referrals')}
                  </Text>
                </div>
              </Link>
            )}
          </Menu.Item>
        )}
        {isLoggedIn ? (
          <Menu.Item>
            {/* biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility */}
            {({ active }) => (
              <button onClick={handleLogout} className="w-full whitespace-nowrap text-xs text-wt-text-on-tertiary">
                <div className="group flex items-center space-x-2 rounded-wt px-3 py-2 hover:bg-black/5">
                  <ArrowLeftStartOnRectangleIcon className="h-3 w-3 text-wt-text-on-tertiary" />
                  <div className="whitespace-nowrap text-xs text-wt-text-on-tertiary sm:block">
                    {t('shared.cta.logout')}
                  </div>
                </div>
              </button>
            )}
          </Menu.Item>
        ) : (
          <Menu.Item>
            {/* biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility */}
            {({ active }) => (
              <button onClick={handleOpenModal} className="w-full whitespace-nowrap text-xs text-wt-text-on-tertiary">
                <div className="group flex w-full items-center space-x-2 rounded-wt px-3 py-2 hover:bg-black/5">
                  <ArrowLeftStartOnRectangleIcon className="h-3 w-3 text-wt-text-on-tertiary" />
                  <div className="whitespace-nowrap text-xs text-wt-text-on-tertiary sm:block">
                    {t('shared.cta.login')}
                  </div>
                </div>
              </button>
            )}
          </Menu.Item>
        )}
      </Dropdown>
    </>
  );
};

export default AccountDropdown;
